import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';

export default function TvetResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [complaintData, setComplaintData] = useState({ category: '', description: '', phoneNumber: '' });
  const [validationError, setValidationError] = useState('');
  const [complaintReference, setComplaintReference] = useState('');
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [isSubmittingComplaint, setIsSubmittingComplaint] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const result = location.state?.resultData;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow text-center">
          <h2 className="text-xl font-bold mb-4 text-red-600">No result data found.</h2>
          <button onClick={() => navigate('/tvet-username')} className="px-6 py-2 bg-blue-700 text-white rounded-lg mt-4">Back</button>
        </div>
      </div>
    );
  }

  // Pass/fail logic: pass if score >= 50
  const score = Number(result['2017_end_year_score']);
  const passed = !isNaN(score) && score >= 50;

  // Remove all complaint and download logic

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/tvet-username')}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            <span>Back</span>
          </button>
        </div>
        {/* Results Header */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h2 className="text-xl font-bold flex items-center mb-4 sm:mb-0" style={{ color: 'rgb(18,82,166)' }}>
              <i className="fa-solid fa-wrench mr-3 text-lg" style={{ color: 'rgb(18,82,166)' }} />
              TVET Exam Result
            </h2>
          </div>
        </section>
        {/* Student Info Card */}
        <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100" style={{ fontFamily: 'Roboto, \"Helvetica Neue\", Arial, sans-serif' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            {[
              { icon: 'fa-user', label: 'Username', value: result.username },
              { icon: 'fa-id-card', label: 'Full Name', value: result.full_name },
              { icon: 'fa-venus-mars', label: 'Gender', value: result.gender },
              { icon: 'fa-building-columns', label: 'Institution', value: result.institution },
              { icon: 'fa-book', label: 'Exam Topic', value: result.exam_topic },
            ].map(({ icon, label, value }) => (
              <div key={label} className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="flex items-center mb-2">
                  <i className={`fa-solid ${icon} mr-2`} style={{ color: 'rgb(18,82,166)' }} />
                  <p className="text-sm text-gray-500">{label}</p>
                </div>
                <p className="font-normal text-gray-900 break-words">{value}</p>
              </div>
            ))}
          </div>
        </section>
        {/* Results Table (Desktop) */}
        <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr style={{ backgroundColor: 'rgb(18,82,166)' }}>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Examination Year</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Score</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <div className="flex items-center">
                      <i className="fa-regular fa-calendar mr-3 text-lg" style={{ color: 'rgb(18,82,166)' }} />
                      <span className="text-sm font-medium text-gray-900">2018 Mid Year</span>
                    </div>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {isNaN(score) ? 'N/A' : score + '/100'}
                      </div>
                      {!isNaN(score) && (
                        <div className="ml-3 w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${score >= 50 ? 'bg-green-600' : 'bg-red-600'}`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                      passed ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                    }`}>
                      <i className={`fa-solid ${passed ? 'fa-check-circle' : 'fa-times-circle'} mr-2`} />
                      {passed ? 'Passed' : 'Failed'}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Mobile Card View */}
          <div className="md:hidden">
            <div className="p-4">
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <i className="fa-regular fa-calendar mr-2 text-lg" style={{ color: 'rgb(18,82,166)' }} />
                    <span className="text-sm font-medium text-gray-700">Examination Year</span>
                  </div>
                </div>
                <p className="text-base font-semibold text-gray-900">2018 Mid Year</p>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Score</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-base font-semibold text-gray-900">{isNaN(score) ? 'N/A' : score + '/100'}</p>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  {!isNaN(score) && (
                    <div
                      className={`h-full rounded-full ${score >= 50 ? 'bg-green-600' : 'bg-red-600'}`}
                      style={{ width: `${score}%` }}
                    />
                  )}
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Status</span>
                </div>
                <div className="space-y-2">
                  <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
                    passed ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                  }`}>
                    <i className={`fa-solid ${passed ? 'fa-check-circle' : 'fa-times-circle'} mr-2`} />
                    {passed ? 'Passed' : 'Failed'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Note Section */}
        <section className="mt-12 p-6 sm:p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 shadow-md">
          <div className="flex items-start">
            <i className="fa-solid fa-circle-info text-2xl flex-shrink-0 mt-1" style={{ color: 'rgb(18,82,166)' }} />
            <div className="ml-4">
              <h4 className="text-lg font-semibold mb-2" style={{ color: 'rgb(18,82,166)' }}>Important Note</h4>
              <p className="text-sm mb-2" style={{ color: 'rgb(18,82,166)' }}>
                This is your official TVET examination result. For any discrepancies or concerns, please contact your institution.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
} 